import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppSelector } from '../../../hooks/hooks';
import { Role } from '../../../features/roles/types';
import TiminatorTableSettings, { TiminatorTableColumn } from '../../../components/shared/table-v2/types';
import { Lx } from '../../../i18n/consts';
import { StatusTag } from '../../../components/shared/status-tag';
import { DeleteIconButton } from '../../../components/shared/delete-icon-button';
import AvatarsList from '../../../components/shared/avatars';
import { getEmployeeNames } from '../../../components/shared/avatars/helpers';
import { normalizeString } from '../../../utils/strings';
import { buildSearchValues, filterByStatusAndSearch, getStatusSortValue, getStatusValue } from '../../../utils/table-filters';
import styles from '../roles.module.scss';


const MAX_VISIBLE_EMPLOYEES = 7;

const renderStatusCell = (rowData: Role) => (
    <StatusTag status={rowData.isActive ? 'active' : 'suspended'}>
        <span>
            <FormattedMessage id={rowData.isActive ? 'active' : 'suspended'} />
        </span>
    </StatusTag>
);

const renderEmployeesCell = (rowData: Role) => (
    <AvatarsList
        items={rowData.employees ?? []}
        visibleCount={MAX_VISIBLE_EMPLOYEES}
    />
);

const renderMobileInfo = (rowData: Role) => (
    <div className={styles.roleMobileInfo}>
        <span className={styles.roleMobileName}>{rowData.name}</span>
        <div className={styles.roleMobileEmployees}>
            {renderEmployeesCell(rowData)}
        </div>
    </div>
);

export const useRolesTable = (
    search: string,
    selectedStatus: number,
    isMobile: boolean
): TiminatorTableSettings<Role> => {
    const intl = useIntl();
    const roles = useAppSelector(store => store.roles.items)

    const columns = useMemo((): TiminatorTableColumn<Role>[] => {
        const getEmployeesSortValue = (role: Role) => {
            const employees = role.employees ?? [];
            return normalizeString(getEmployeeNames(employees));
        };
        if (isMobile) {
            return [
                {
                    id: 'mobileInfo',
                    label: intl.formatMessage({ id: Lx.General.NAME }),
                    width: '1fr',
                    format: renderMobileInfo,
                },
                {
                    id: 'status',
                    label: intl.formatMessage({ id: Lx.General.STATUS }),
                    align: 'right',
                    width: '25%',
                    sortable: true,
                    getSortValue: (rowData: Role) => getStatusSortValue(rowData.isActive),
                    format: renderStatusCell,
                },
                {
                    id: 'deleteButton',
                    align: 'right',
                    width: '24px',
                    format: () => <DeleteIconButton />,
                },
            ];
        }

        return [
            {
                id: 'name',
                label: intl.formatMessage({ id: Lx.General.NAME }),
                width: '1fr',
                sortable: true,
                getSortValue: (rowData: Role) => normalizeString(rowData.name),
                format: (rowData: Role) => rowData.name,
            },
            {
                id: 'employees',
                label: intl.formatMessage({ id: Lx.General.EMPLOYEES }),
                width: '1fr',
                sortable: true,
                getSortValue: getEmployeesSortValue,
                format: renderEmployeesCell,
            },
            {
                id: 'status',
                label: intl.formatMessage({ id: Lx.General.STATUS }),
                align: 'left',
                width: '160px',
                sortable: true,
                getSortValue: (rowData: Role) => getStatusSortValue(rowData.isActive),
                format: renderStatusCell,
            },
            {
                id: 'deleteButton',
                align: 'right',
                width: '64px',
                format: () => <DeleteIconButton />,
            },
        ];
    }, [intl, isMobile]);

    const data = useMemo(() => (
        filterByStatusAndSearch(
            roles,
            selectedStatus,
            role => role.isActive,
            search,
            role => {
                const employeeNames = getEmployeeNames(role.employees ?? []);

                return buildSearchValues([
                    role.name,
                    employeeNames,
                    getStatusValue(intl, role.isActive),
                ]);
            }
        )
    ), [roles, search, selectedStatus, intl]);

    const isEmpty = roles.length === 0;

    return {
        columns,
        data,
        isEmpty,
    };
};
